import React, { useEffect, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { RotateCcw } from "lucide-react";

import StepHeader from "../StepHeader";
import { Button } from "@/components/ui/Button";
import RHFInput from "@/components/form/RHFInput";
import RHFSelect from "@/components/form/RHFSelect";
import AlertModal from "@/components/shared/AlertModal";
import { NOTICE_PERIOD_OPTIONS } from "@/config/dropdownData";
import { calculateTotalExperience } from "../../utils/calculateExperience";
import { getDefaultCurrencyFallback } from "@/hooks/useDefaultCurrency";

const DIGITS = /^\d+$/;

// Both salaries filled, and expected strictly below current. Anything the
// schema would refuse is left to the schema's own message.
const isExpectedBelowCurrent = (current, expected) => {
  const cur = String(current ?? "").trim();
  const exp = String(expected ?? "").trim();
  return DIGITS.test(cur) && DIGITS.test(exp) && Number(exp) < Number(cur);
};

const StepSalary = ({
  mandatoryFields = [],
  autoCalculate = true,
  disabled = false,
  salaryFieldsRequired = true,
  showSalaryDetails = true,
  currencySign: propCurrencySign,
}) => {
  const { control, setValue, getValues, setFocus } = useFormContext();
  const isManualOverride = useRef(false);

  // An expected salary below the current one is nearly always a slip — a
  // missing zero, or the two boxes swapped — so leaving the salary fields asks
  // once. It is a question, not a refusal: a confirmed pair is not asked again.
  const [lowerExpectedOpen, setLowerExpectedOpen] = useState(false);
  const confirmedPair = useRef(null);

  const salaryPair = () => {
    const { current_salary, expected_salary } = getValues("compensation") ?? {};
    return { current: current_salary, expected: expected_salary, key: `${current_salary}|${expected_salary}` };
  };

  // Fires when focus leaves both salary inputs, not when it moves from one to
  // the other, so the question comes after the second figure is typed.
  const handleSalaryBlur = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    const { current, expected, key } = salaryPair();
    if (isExpectedBelowCurrent(current, expected) && confirmedPair.current !== key) {
      setLowerExpectedOpen(true);
    }
  };

  const keepLowerExpected = () => {
    confirmedPair.current = salaryPair().key;
    setLowerExpectedOpen(false);
  };

  const changeLowerExpected = () => {
    setLowerExpectedOpen(false);
    // After the dialog has closed and handed focus back.
    setTimeout(() => setFocus("compensation.expected_salary"), 0);
  };

  const formCurrencySign = useWatch({
    control,
    name: "currency_sign",
  });

  const currencySign =
    propCurrencySign ??
    formCurrencySign ??
    getDefaultCurrencyFallback().currencySign;

  const isFieldRequired = (fieldNames) =>
    mandatoryFields.some((f) =>
      fieldNames.some((fn) => f.endsWith(fn) || f === fn)
    );

  const isCurrentSalaryRequired =
    salaryFieldsRequired &&
    isFieldRequired([
      "current_salary",
      "compensation.current_salary",
    ]);

  const isExpectedSalaryRequired =
    salaryFieldsRequired &&
    isFieldRequired([
      "expected_salary",
      "compensation.expected_salary",
    ]);

  const isNoticePeriodRequired =
    salaryFieldsRequired &&
    isFieldRequired([
      "notice_period",
      "compensation.notice_period",
    ]);

  const isTotalExperienceRequired = isFieldRequired([
    "total_experience",
    "compensation.total_experience",
  ]);

  const watchedExperience = useWatch({
    control,
    name: "experience",
  });

  const watchedTotalExp = useWatch({
    control,
    name: "compensation.total_experience",
  });

  const calculated = autoCalculate
    ? calculateTotalExperience(watchedExperience)
    : "";

  useEffect(() => {
    if (!autoCalculate) return;
    if (isManualOverride.current) return;

    if (calculated !== "") {
      setValue(
        "compensation.total_experience",
        String(calculated),
        {
          shouldDirty: false,
        }
      );
    }
  }, [autoCalculate, watchedExperience, calculated, setValue]);

  const handleManualChange = () => {
    isManualOverride.current = true;
  };

  const handleReset = () => {
    isManualOverride.current = false;

    if (calculated !== "") {
      setValue(
        "compensation.total_experience",
        String(calculated),
        {
          shouldDirty: true,
        }
      );
    }
  };

  const isOverridden =
    isManualOverride.current &&
    String(watchedTotalExp) !== String(calculated);

  return (
    <div>
      <StepHeader title="Salary & Availability" />

      <div className="space-y-5 px-5">
        {/* Availability */}
        {showSalaryDetails && (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-text-secondary">
              Availability
            </h3>

            <RHFSelect
              name="compensation.notice_period"
              label="Notice Period"
              placeholder="Select notice period"
              options={NOTICE_PERIOD_OPTIONS}
              required={isNoticePeriodRequired}
              disabled={disabled}
            />
          </div>
        )}

        {/* Total Experience */}
        <div
          className={
            showSalaryDetails
              ? "space-y-1"
              : "rounded-xl p-4 space-y-1"
          }
        >
          <RHFInput
            name="compensation.total_experience"
            label="Total Experience (years)"
            placeholder="e.g. 5.5"
            required={isTotalExperienceRequired}
            onChange={handleManualChange}
            disabled={disabled}
          />

          <div className="flex items-center justify-between min-h-[20px]">
            {calculated !== "" && (
              <p className="text-[11px] text-text-secondary">
                Auto-calculated from your entries:{" "}
                <span className="font-medium text-text-primary">
                  {calculated} yrs
                </span>
              </p>
            )}

            {autoCalculate && isOverridden && (
              <Button
                type="button"
                onClick={handleReset}
                className="ml-auto flex items-center gap-1 rounded-none p-0 text-[11px] text-blue-400 hover:text-blue-300 transition"
              >
                <RotateCcw size={11} />
                Reset to calculated
              </Button>
            )}
          </div>
        </div>

        <hr className="border-border-primary" />

        {/* Salary Details */}
        {showSalaryDetails && (
          <div className="rounded-xl p-4 space-y-4">
            <h3 className="text-sm font-medium text-text-secondary">
              Salary Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" onBlur={handleSalaryBlur}>
              <RHFInput
                name="compensation.current_salary"
                label={`Gross Salary (${currencySign})`}
                placeholder="15000"
                required={isCurrentSalaryRequired}
                disabled={disabled}
              />

              <RHFInput
                name="compensation.expected_salary"
                label={`Expected Gross Salary (${currencySign})`}
                placeholder="18000"
                required={isExpectedSalaryRequired}
                disabled={disabled}
              />
            </div>
          </div>
        )}
      </div>

      <AlertModal
        open={lowerExpectedOpen}
        title="Expected salary is lower than current"
        description={`The expected gross salary (${currencySign} ${salaryPair().expected}) is less than the current gross salary (${currencySign} ${salaryPair().current}). Is that right?`}
        cancelText="Change it"
        actionText="Yes, it's correct"
        onCancel={changeLowerExpected}
        onAction={keepLowerExpected}
        onOpenChange={(open) => !open && setLowerExpectedOpen(false)}
      />
    </div>
  );
};

export default StepSalary;